<script setup lang="ts">
/**
 * PricingView — 1:1 порт docs/mockups/public-pricing.html (inbox-161).
 *
 * Спека: docs/specs/2026-05-19-public-behavior.md §28 «Тарифы».
 * STYLE_NOTES: hero padding 88px / hero-title 56px / gradient text / section 80px.
 *
 * Секции:
 *  1. Hero с badge + gradient title + sub + billing toggle (Monthly/Yearly)
 *  2. 3-tier pricing-grid (Free / Pro [990] / Business [featured, 4990])
 *     + Enterprise секция (mailto). Compare table показывает все 4 для reference.
 *  3. Comparison table (4 категории × 14 фичей × 4 тарифа)
 *  4. FAQ accordion (8 вопросов)
 *  5. Enterprise CTA
 *  6. Final CTA
 *
 * Reactive: billingPeriod toggle пересчитывает price-amount (yearly = -20%).
 * Spec §28 — yearly discount A/B-testable; default 20% per mockup.
 */
import { computed, onMounted, ref } from 'vue';
import AppPublic from '@/layouts/AppPublic.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import { useMeStore } from '@/stores/me';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'Тарифы — от 990 ₽/мес',
  description:
    'Free, Старт, Бизнес, Команда. Сравнение возможностей, FAQ, переход на Enterprise. Российские серверы, 152-ФЗ, чеки и закрывающие документы.',
  path: '/pricing',
  type: 'website',
  jsonLd: productJsonLd(
    'Тарифы — link-platform',
    'Free / Старт / Бизнес / Команда. Hosted в РФ, 152-ФЗ compliance, чеки и НДС.',
  ),
});

// ── Billing period toggle ────────────────────────────────────────
type Billing = 'monthly' | 'yearly';
const billing = ref<Billing>('monthly');
const YEARLY_DISCOUNT = 0.20;  // spec §28 A/B-testable; default 20%

interface Tier {
  slug: 'free' | 'pro' | 'business' | 'team';
  name: string;
  monthly: number;          // ₽/мес
  desc: string;
  features: string[];
  cta: string;
  ctaVariant: 'outline' | 'primary';
  featured?: boolean;
}

// inbox-363 — 3 tier структура per Sprint A блокер монетизации:
// Free / Pro 990₽ / Business 4990₽. Команда / Enterprise — отдельная
// Enterprise секция ниже (mailto:sales@). Compare table показывает
// все 4 для справки.
const TIERS: Tier[] = [
  {
    slug: 'free',
    name: 'Free',
    monthly: 0,
    desc: 'Попробовать без обязательств',
    features: [
      '50 ссылок в месяц',
      'Базовая аналитика',
      'QR-коды (статические)',
      '1 пользователь',
      'Сообщество и документация',
    ],
    cta: 'Начать',
    ctaVariant: 'outline',
  },
  {
    slug: 'pro',
    name: 'Pro',
    monthly: 990,
    desc: 'Для ИП и соло-маркетологов',
    features: [
      '500 ссылок в месяц',
      'QR с лого',
      'Bio-страница',
      'Smart Time (AI-рекомендации)',
      'Email-поддержка',
    ],
    cta: 'Выбрать Pro',
    ctaVariant: 'outline',
  },
  {
    slug: 'business',
    name: 'Business',
    monthly: 4990,
    desc: 'Performance-команды и СМБ',
    features: [
      '5 000 ссылок в месяц',
      'AI-копайлот без лимитов',
      'A/B-тесты и Smart Routing',
      '1 кастомный домен',
      'REST API + Webhooks',
      'Антифрод ML',
      'Поддержка в TG',
    ],
    cta: 'Выбрать Business',
    ctaVariant: 'primary',
    featured: true,
  },
];

function priceFor(tier: Tier): string {
  if (tier.monthly === 0) return '0 ₽';
  const m = billing.value === 'yearly'
    ? Math.round(tier.monthly * (1 - YEARLY_DISCOUNT))
    : tier.monthly;
  // ru-RU thousand-separator с не-разрывным пробелом
  return m.toLocaleString('ru-RU').replace(/,/g, ' ') + ' ₽';
}

// inbox-363 — auth-aware CTA: гость → /signup (with plan hint),
// authed → /settings/billing (для upgrade flow).
const me = useMeStore();
onMounted(() => {
  if (!me.initialized && typeof window !== 'undefined') void me.load();
});

function ctaHref(tier: Tier): string {
  if (me.isAuthenticated) {
    return tier.slug === 'free' ? '/dashboard' : `/settings/billing?plan=${tier.slug}`;
  }
  return tier.slug === 'free' ? '/signup' : `/signup?plan=${tier.slug}`;
}

function ctaLabel(tier: Tier): string {
  if (me.isAuthenticated && tier.slug !== 'free') return `Перейти на ${tier.name}`;
  if (me.isAuthenticated && tier.slug === 'free') return 'Открыть кабинет';
  return tier.cta;
}

// ── FAQ accordion ─────────────────────────────────────────────────
interface FaqItem {
  q: string;
  a: string;
  open: boolean;
}

const faq = ref<FaqItem[]>([
  {
    q: 'Как платить — карта, СБП, безналом?',
    a: 'Принимаем карты МИР, Visa и Mastercard (выпущенные в России), оплату по СБП и безналичный расчёт для юр.лиц. На каждое списание выставляем чек ОФД и закрывающие документы (счёт + акт + счёт-фактура для НДС).',
    open: true,
  },
  {
    q: 'Можно перейти между тарифами в середине месяца?',
    a: 'Да. При апгрейде доплачиваете пропорциональную часть разницы за остаток месяца. При даунгрейде — новый тариф применится со следующего расчётного периода, не теряя текущий.',
    open: false,
  },
  {
    q: 'Что произойдёт при превышении лимита?',
    a: 'Уведомим на 80% от лимита. На 100% — создание новых ссылок остановится, но существующие будут работать. Можно докупить пакеты (10 ₽ / 1 000 кликов сверху) или повысить тариф одним кликом.',
    open: false,
  },
  {
    q: 'Есть ли скидки для НКО и образовательных учреждений?',
    a: 'Да. Подтверждённым НКО, школам и вузам предоставляем 50% скидку на любой тариф. Напишите на nko@link-platform.ru — пришлём промокод.',
    open: false,
  },
  {
    q: 'Где хранятся данные и соблюдается ли ФЗ-152?',
    a: 'Все персональные данные хранятся на серверах Yandex Cloud в Москве и Санкт-Петербурге. Платформа уведомлена в Роскомнадзоре как оператор ПДн, применяет шифрование at-rest. Раз в год проходим внешний security-аудит.',
    open: false,
  },
  {
    q: 'Возврат денег возможен?',
    a: 'Да, в первые 14 дней после оплаты вернём полную сумму без вопросов. Дальше — пропорционально неиспользованному периоду.',
    open: false,
  },
  {
    q: 'Что считается одной активной ссылкой?',
    a: 'Любая короткая ссылка в статусе «активна» в течение календарного месяца — одна позиция в лимите. Архивированные и истёкшие ссылки в лимит не входят.',
    open: false,
  },
  {
    q: 'Есть ли бесплатный период для платных тарифов?',
    a: '14 дней триала на тарифах Старт и Бизнес — без привязки карты. Команда — по запросу через продажи.',
    open: false,
  },
]);

function toggleFaq(idx: number) {
  faq.value[idx].open = !faq.value[idx].open;
}
</script>

<template>
  <AppPublic>
    <!-- HERO -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">
            Без скрытых платежей · Чеки и закрывающие документы
          </div>
          <h1 class="hero-title text-[30px] sm:text-[44px] lg:text-[56px]">
            Тарифы, которые<br />
            <span class="gradient">растут с бизнесом</span>
          </h1>
          <p class="hero-sub">
            Начните бесплатно, расширяйтесь по мере спроса. Все тарифы включают
            российские серверы, антифрод и AI-копайлот.
          </p>
          <div class="billing-toggle">
            <button
              type="button"
              :class="{ active: billing === 'monthly' }"
              @click="billing = 'monthly'"
            >
              Ежемесячно
            </button>
            <button
              type="button"
              :class="{ active: billing === 'yearly' }"
              @click="billing = 'yearly'"
            >
              Годовое <span class="save-badge">−20%</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- PRICING GRID -->
    <section class="pricing-section">
      <div class="container">
        <div class="pricing-grid">
          <article
            v-for="tier in TIERS"
            :key="tier.slug"
            :class="['price-card', { featured: tier.featured }]"
          >
            <UiBadge v-if="tier.featured" variant="success" class="featured-badge">
              Популярный
            </UiBadge>
            <div class="tier-name">{{ tier.name }}</div>
            <div class="price">
              {{ priceFor(tier) }}<small>/мес</small>
            </div>
            <div class="price-desc">{{ tier.desc }}</div>
            <ul>
              <li v-for="feat in tier.features" :key="feat">{{ feat }}</li>
            </ul>
            <a
              :href="ctaHref(tier)"
              :class="['btn btn-lg', tier.ctaVariant === 'primary' ? 'btn-primary' : 'btn-outline']"
            >
              {{ ctaLabel(tier) }}
            </a>
          </article>
        </div>
      </div>
    </section>

    <!-- COMPARISON TABLE -->
    <section class="compare-wrap">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Сравнение</span>
          <h2>Что входит в каждый тариф</h2>
          <p class="section-sub">
            Все тарифы — серверы в России, 152-ФЗ, SLA 99.9%. Различия только в лимитах
            и продвинутых функциях.
          </p>
        </div>

        <!-- inbox-362 — outer обёртка для absolute scroll-hint. -->
        <div class="compare-scroll-outer">
          <div class="compare-scroll">
            <table class="compare">
            <thead>
              <tr>
                <th style="width: 36%;">Возможность</th>
                <th>Free</th>
                <th>Старт</th>
                <th class="featured">Бизнес</th>
                <th>Команда</th>
              </tr>
            </thead>
            <tbody>
              <!-- ЛИМИТЫ -->
              <tr class="section-row"><td colspan="5">Лимиты</td></tr>
              <tr><td>Активных ссылок</td><td>50</td><td>500</td><td class="featured">5 000</td><td>50 000</td></tr>
              <tr><td>Кликов в месяц</td><td>10 000</td><td>100 000</td><td class="featured">1 000 000</td><td>Без лимита</td></tr>
              <tr><td>Хранение аналитики</td><td>30 дней</td><td>90 дней</td><td class="featured">12 месяцев</td><td>36 месяцев</td></tr>

              <!-- AI -->
              <tr class="section-row"><td colspan="5">AI и автоматизация</td></tr>
              <tr><td>AI-копайлот (YandexGPT)</td><td><span class="dash">—</span></td><td>50 запросов / мес</td><td class="featured">Без лимитов</td><td>Без лимитов</td></tr>
              <tr><td>Smart Time</td><td><span class="dash">—</span></td><td><span class="check">✓</span></td><td class="featured"><span class="check">✓</span></td><td><span class="check">✓</span></td></tr>
              <tr><td>A/B-тесты + Smart Routing</td><td><span class="dash">—</span></td><td><span class="dash">—</span></td><td class="featured"><span class="check">✓</span></td><td><span class="check">✓</span></td></tr>
              <tr><td>Антифрод ML</td><td>Базовый</td><td>Базовый</td><td class="featured">Расширенный</td><td>Расширенный</td></tr>

              <!-- БРЕНДИНГ -->
              <tr class="section-row"><td colspan="5">Брендинг</td></tr>
              <tr><td>Кастомные домены</td><td><span class="dash">—</span></td><td><span class="dash">—</span></td><td class="featured">1</td><td>5</td></tr>
              <tr><td>QR с лого и брендингом</td><td><span class="dash">—</span></td><td><span class="check">✓</span></td><td class="featured"><span class="check">✓</span></td><td><span class="check">✓</span></td></tr>
              <tr><td>White-label</td><td><span class="dash">—</span></td><td><span class="dash">—</span></td><td class="featured"><span class="dash">—</span></td><td><span class="check">✓</span></td></tr>

              <!-- КОМАНДА -->
              <tr class="section-row"><td colspan="5">Команда и интеграции</td></tr>
              <tr><td>Пользователей</td><td>1</td><td>1</td><td class="featured">5</td><td>Без лимита</td></tr>
              <tr><td>SSO (Yandex 360 / VK ID)</td><td><span class="dash">—</span></td><td><span class="dash">—</span></td><td class="featured"><span class="dash">—</span></td><td><span class="check">✓</span></td></tr>
              <tr><td>REST API + Webhooks</td><td><span class="dash">—</span></td><td>Read-only</td><td class="featured"><span class="check">✓</span></td><td><span class="check">✓</span></td></tr>
              <tr><td>Интеграции (Метрика, AmoCRM…)</td><td><span class="check">✓</span></td><td><span class="check">✓</span></td><td class="featured"><span class="check">✓</span></td><td><span class="check">✓</span></td></tr>

              <!-- ПОДДЕРЖКА -->
              <tr class="section-row"><td colspan="5">Поддержка</td></tr>
              <tr><td>Канал поддержки</td><td>Сообщество</td><td>Email</td><td class="featured">Email + TG</td><td>TG + менеджер</td></tr>
              <tr><td>SLA реакции</td><td>—</td><td>48 часов</td><td class="featured">8 часов</td><td>1 час</td></tr>
            </tbody>
            </table>
          </div>
          <span class="scroll-hint" aria-hidden="true">→</span>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq-section">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Частые вопросы</span>
          <h2>Что чаще всего спрашивают</h2>
          <p class="section-sub">
            Не нашли ответ? Напишите нам в TG или на info@link-platform.ru —
            отвечаем в течение часа.
          </p>
        </div>
        <div class="faq">
          <div
            v-for="(item, idx) in faq"
            :key="idx"
            :class="['faq-item', { collapsed: !item.open }]"
          >
            <button type="button" class="faq-q" @click="toggleFaq(idx)" :aria-expanded="item.open">
              {{ item.q }}<span class="chev" aria-hidden="true"></span>
            </button>
            <div v-show="item.open" class="faq-a">
              {{ item.a }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ENTERPRISE CTA -->
    <section class="enterprise-section">
      <div class="container">
        <div class="enterprise">
          <div>
            <h3>Нужно больше? Поговорим о Enterprise</h3>
            <p>
              On-premise развёртывание, SSO (SAML, LDAP), кастомные интеграции с 1С
              и SAP, dedicated менеджер, SLA 99.99%, индивидуальный security-аудит.
              От 50 000 ₽/мес.
            </p>
          </div>
          <div class="enterprise-cta">
            <a href="mailto:sales@link-platform.ru" class="btn btn-primary big">
              Связаться с продажами
            </a>
            <span class="sla-note">Ответим в течение 1 рабочего дня</span>
          </div>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="final-cta">
      <div class="container">
        <div class="final-content">
          <h2>Начните за 60 секунд</h2>
          <p>14 дней триала тарифов Старт и Бизнес — без привязки карты.</p>
          <a href="/signup" class="btn btn-primary big">Попробовать бесплатно</a>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ─── HERO (STYLE_NOTES §1) ─────────────────────────────────────── */
.hero {
  position: relative;
  padding: 88px 0 64px;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.15), transparent 70%),
    radial-gradient(ellipse 50% 40% at 80% 30%, rgba(124, 92, 255, 0.10), transparent 70%);
  pointer-events: none;
}
.hero-content {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  margin-bottom: 24px;
  background: rgba(79, 140, 255, 0.10);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px;
  color: #93b9ff;
  font-weight: 500;
}
.hero-title {
  /* font-size — Tailwind utility text-[30px] sm:text-[44px] lg:text-[56px] (inbox-285) */
  line-height: 1.08;
  letter-spacing: -0.025em;
  font-weight: 700;
  margin-bottom: 24px;
}
.hero-title .gradient {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 60%, #b366ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
.hero-sub {
  font-size: 19px;
  line-height: 1.55;
  color: #94a3b8;
  max-width: 620px;
  margin: 0 auto 36px;
}

/* Billing toggle */
.billing-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}
.billing-toggle button {
  /* inbox-428 — h=40 per spec (соответствует системе input/UiButton.size-md). */
  appearance: none;
  background: transparent;
  border: none;
  color: #94a3b8;
  min-height: 40px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.billing-toggle button:hover {
  color: #e2e8f0;
}
.billing-toggle button.active {
  background: rgba(79, 140, 255, 0.18);
  color: #e2e8f0;
}
.save-badge {
  display: inline-block;
  background: rgba(34, 197, 94, 0.18);
  color: #4ade80;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
}

/* ─── SECTION (STYLE_NOTES §2) ──────────────────────────────────── */
.pricing-section,
.compare-wrap,
.faq-section,
.enterprise-section,
.final-cta {
  padding: 80px 0;
}
.section-head {
  text-align: center;
  margin-bottom: 56px;
}
.section-eyebrow {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #93b9ff;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
}
h2 {
  font-size: 38px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  margin-bottom: 14px;
}
.section-sub {
  font-size: 17px;
  color: #94a3b8;
  max-width: 580px;
  margin: 0 auto;
  line-height: 1.55;
}

/* ─── PRICING GRID ──────────────────────────────────────────────── */
/* inbox-285 — mobile-first 1 / 2 / 3 cols. 4-я карточка переходит на
   новый ряд при 3 cols — норма (acceptance явно 1/2/3 по breakpoints). */
.pricing-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 768px) {
  .pricing-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; }
}
@media (min-width: 1024px) {
  .pricing-grid { grid-template-columns: repeat(3, 1fr); }
}
.price-card {
  position: relative;
  background: #161a23;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
}
.price-card.featured {
  background: linear-gradient(180deg, rgba(79, 140, 255, 0.08), #161a23 60%);
  border-color: rgba(79, 140, 255, 0.35);
  transform: translateY(-8px);
}
.featured-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
}
.price-card .tier-name {
  font-size: 15px;
  color: #94a3b8;
  margin-bottom: 12px;
  font-weight: 500;
}
.price-card .price {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1;
  color: #e2e8f0;
}
.price-card .price small {
  font-size: 15px;
  color: #94a3b8;
  font-weight: 500;
  margin-left: 4px;
}
.price-card .price-desc {
  font-size: 14px;
  color: #94a3b8;
  margin: 10px 0 24px;
  min-height: 42px;
}
.price-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.price-card ul li {
  font-size: 14px;
  color: #cbd5e1;
  line-height: 1.5;
  padding-left: 22px;
  position: relative;
}
.price-card ul li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #4ade80;
  font-weight: 700;
}

/* Buttons — local (без зависимости от UiButton variant API для outline на anchor'е).
   inbox-428 — все три CTA кнопок на price-card имеют единую высоту 48.
   Активный план выделяется border-color + gradient bg, не размером. */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  min-height: 48px;
  padding: 12px 22px;
  border-radius: 8px;
  text-decoration: none;
  border: 1px solid transparent;
  transition: all 0.15s;
  cursor: pointer;
}
.btn-primary {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  color: #fff;
}
.btn-primary:hover {
  filter: brightness(1.08);
}
.btn-outline {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}
.btn-outline:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.20);
}
.btn.big, .btn.btn-lg {
  /* inbox-363 — ui-btn-lg per spec (h=48, padding 12/22). */
  min-height: 48px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 600;
}

/* ─── COMPARE TABLE ─────────────────────────────────────────────── */
.compare-wrap {
  background: rgba(255, 255, 255, 0.015);
}
/* inbox-362 — горизонтальный scroll с min-width на таблице. На <768px
   viewport таблица шире viewport → wrapper скроллится. .scroll-hint
   абсолютный gradient-mask справа подсказывает что есть ещё контент. */
.compare-scroll-outer { position: relative; }
.compare-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.compare-scroll-outer .scroll-hint {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 36px;
  background: linear-gradient(to right, transparent, rgba(10, 13, 18, 0.92));
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 4px;
  font-size: 14px;
  color: #6f9eff;
}
@media (min-width: 768px) {
  .compare-scroll-outer .scroll-hint { display: none; }
}
.compare {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 14px;
}
@media (max-width: 540px) {
  .compare { font-size: 12.5px; }
  .compare th, .compare td { padding: 10px 8px; }
}
.compare th,
.compare td {
  padding: 12px 14px;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.compare th:first-child,
.compare td:first-child {
  text-align: left;
  color: #cbd5e1;
}
.compare thead th {
  font-weight: 600;
  color: #94a3b8;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.02);
}
.compare thead th.featured {
  background: rgba(79, 140, 255, 0.10);
  color: #93b9ff;
}
.compare td.featured {
  background: rgba(79, 140, 255, 0.04);
  font-weight: 500;
  color: #e2e8f0;
}
.section-row td {
  text-align: left !important;
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  color: #94a3b8;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.check {
  color: #4ade80;
  font-weight: 700;
}
.dash {
  color: #475569;
}

/* ─── FAQ ───────────────────────────────────────────────────────── */
.faq {
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.faq-item {
  background: #161a23;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}
.faq-q {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  width: 100%;
  appearance: none;
  background: transparent;
  border: none;
  color: #e2e8f0;
  font-size: 15px;
  font-weight: 500;
  padding: 18px 22px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}
.faq-q:hover {
  background: rgba(255, 255, 255, 0.02);
}
.chev {
  width: 12px;
  height: 12px;
  border-right: 2px solid #94a3b8;
  border-bottom: 2px solid #94a3b8;
  transform: rotate(45deg);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.faq-item.collapsed .chev {
  transform: rotate(-45deg);
}
.faq-a {
  padding: 0 22px 18px;
  font-size: 14px;
  line-height: 1.6;
  color: #94a3b8;
}

/* ─── ENTERPRISE ────────────────────────────────────────────────── */
.enterprise {
  background: linear-gradient(135deg, rgba(79, 140, 255, 0.08), rgba(124, 92, 255, 0.06));
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 16px;
  padding: 40px 44px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  align-items: center;
}
.enterprise h3 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 0 0 12px;
  color: #e2e8f0;
}
.enterprise p {
  font-size: 15px;
  line-height: 1.6;
  color: #94a3b8;
  margin: 0;
}
.enterprise-cta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}
.sla-note {
  font-size: 13px;
  color: #64748b;
}

/* ─── FINAL CTA ─────────────────────────────────────────────────── */
.final-cta {
  text-align: center;
}
.final-content {
  max-width: 600px;
  margin: 0 auto;
}
.final-content h2 {
  margin-bottom: 12px;
}
.final-content p {
  font-size: 17px;
  color: #94a3b8;
  margin-bottom: 28px;
}

/* ─── RESPONSIVE ────────────────────────────────────────────────── */
/* inbox-285 — pricing-grid правила вынесены в основной блок (mobile-first).
   hero-title font-size управляется Tailwind utility. Здесь — оставшиеся
   tweaks по padding / typography. */
@media (max-width: 1023px) {
  .price-card.featured {
    transform: none;
  }
  .enterprise {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .enterprise-cta {
    align-items: center;
  }
}

@media (max-width: 980px) {
  .container {
    padding: 0 24px;
  }
}

@media (max-width: 640px) {
  .hero {
    padding: 48px 0 40px;
  }
  .hero-sub {
    font-size: 15.5px;
    margin-bottom: 28px;
  }
  .pricing-section,
  .compare-wrap,
  .faq-section,
  .enterprise-section,
  .final-cta {
    padding: 56px 0;
  }
  .section-head {
    margin-bottom: 36px;
  }
  h2 {
    font-size: 26px;
  }
  .section-sub {
    font-size: 15px;
  }
  .enterprise {
    padding: 28px 20px;
  }
  .enterprise h3 {
    font-size: 22px;
  }
  .container {
    padding: 0 18px;
  }
}
</style>
